import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { slugify } from '@publiqa/shared'
import type { ArticleSummary, Contributor } from '@publiqa/shared'
import { useFetch } from '../lib/useFetch'
import { Card } from './blocks/Journal'
import { SmartLink, usePublic, usePubliqApi } from './context'
import { useHead } from './Layout'
import { ArticleCard, Loading, NotFound, Picture } from './ui'

/**
 * « Contributeurs » : les auteurs du journal, chacun avec sa fiche (photo,
 * fonction, biographie), son nombre d'articles et ses dernières contributions.
 */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

export function ContributorAvatar({
  c,
  size = 56,
}: {
  c: Pick<Contributor, 'name' | 'photo'>
  size?: number
}) {
  return (
    <span className="ctb-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {c.photo ? (
        <Picture image={c.photo} alt={c.name} />
      ) : (
        <span aria-hidden="true">{initials(c.name)}</span>
      )}
    </span>
  )
}

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''}`
const excerpt = (text: string, max = 150) =>
  text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text

type Sort = 'most' | 'recent' | 'az' | 'za' | 'least'

/** Lien vers la fiche d'un auteur (nom tel qu'il signe ses articles). */
export const contributorHref = (name: string) => `/contributeurs/${slugify(name) || 'auteur'}`

export function ContributorsRoute() {
  const { publication } = usePublic()
  const path = usePubliqApi()
  const data = useFetch<{ contributors: Contributor[]; articles: number }>(path('/contributors'))
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('most')
  useHead(`Contributeurs`, `Les auteurs et contributeurs de ${publication.name}`)
  const list = useMemo(() => {
    const all = data.data?.contributors ?? []
    const needle = q.trim().toLowerCase()
    const found = needle
      ? all.filter((c) => `${c.name} ${c.role} ${c.bio}`.toLowerCase().includes(needle))
      : all
    const by = {
      most: (a: Contributor, b: Contributor) => b.articles - a.articles,
      least: (a: Contributor, b: Contributor) => a.articles - b.articles,
      recent: (a: Contributor, b: Contributor) =>
        (b.lastPublishedAt ?? '').localeCompare(a.lastPublishedAt ?? ''),
      az: (a: Contributor, b: Contributor) => a.name.localeCompare(b.name, 'fr'),
      za: (a: Contributor, b: Contributor) => b.name.localeCompare(a.name, 'fr'),
    }[sort]
    return [...found].sort((a, b) => by(a, b) || a.name.localeCompare(b.name, 'fr'))
  }, [data.data, q, sort])

  if (data.loading && !data.data) return <Loading />
  const count = data.data?.contributors.length ?? 0
  const articles = data.data?.articles ?? 0
  return (
    <div className="ctb">
      <header className="container ctb-head">
        <div className="ctb-intro">
          <p className="ctb-eyebrow">Les plumes du journal</p>
          <h1>Auteurs et contributeurs de {publication.name}</h1>
          <p>
            Découvrez les <b>{count}</b> auteurs et contributeurs qui font vivre {publication.name}{' '}
            :{' '}
            <b>
              {articles} article{articles > 1 ? 's publiés' : ' publié'}
            </b>
            .
          </p>
        </div>
        <div className="ctb-stats">
          <div>
            <strong>{count}</strong>
            <span>auteurs et contributeurs</span>
          </div>
          <div>
            <strong>{articles}</strong>
            <span>articles publiés</span>
          </div>
          <SmartLink href="/actualites" className="ctb-all">
            <strong>Tous les articles</strong>
            <span>Le fil complet, du plus récent au plus ancien →</span>
          </SmartLink>
        </div>
      </header>

      <div className="container ctb-tools">
        <label className="ctb-search">
          <span>Rechercher un auteur</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, prénom, spécialité…"
          />
        </label>
        <label className="ctb-sort">
          <span>Trier</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="most">Les plus prolifiques</option>
            <option value="recent">Derniers publiants</option>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
            <option value="least">Les moins prolifiques</option>
          </select>
        </label>
      </div>

      <div className="container ctb-grid">
        {list.map((c) => (
          <article key={c.slug} className="ctb-card">
            <header>
              <ContributorAvatar c={c} />
              <div>
                <h2>
                  <SmartLink href={`/contributeurs/${c.slug}`}>{c.name}</SmartLink>
                </h2>
                <p className="ctb-count">{plural(c.articles, 'article')}</p>
              </div>
            </header>
            {(c.role || c.bio) && <p className="ctb-bio">{excerpt(c.role || c.bio)}</p>}
            {c.recent.length > 0 && (
              <div className="ctb-recent">
                <p>Contributions récentes</p>
                <ul>
                  {c.recent.map((r) => (
                    <li key={r.slug}>
                      <SmartLink href={`/article/${r.slug}`}>{r.title}</SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <SmartLink href={`/contributeurs/${c.slug}`} className="ctb-more">
              Voir la fiche →
            </SmartLink>
          </article>
        ))}
        {data.data && !list.length && <p className="ctb-empty">Aucun auteur ne correspond.</p>}
      </div>
    </div>
  )
}

/** Fiche d'un auteur : présentation et tous ses articles. */
export function ContributorRoute() {
  const { contributor: slug = '' } = useParams()
  const { publication } = usePublic()
  const path = usePubliqApi()
  const isMag = publication.kind === 'magazine'
  const data = useFetch<{ contributor: Contributor }>(path(`/contributors/${slug}`))
  const c = data.data?.contributor
  const articles = useFetch<{ articles: ArticleSummary[]; total: number }>(
    c ? path('/articles', { author: c.name, limit: 50 }) : null,
  )
  useHead(c?.name, c?.role || c?.bio)
  if (data.loading && !data.data) return <Loading />
  if (data.error || !c) return <NotFound message="Ce contributeur n’existe pas ou plus." />
  return (
    <div className="ctb ctb-profile">
      <div className="container">
        <SmartLink href="/contributeurs" className="ctb-back">
          ← Tous les contributeurs
        </SmartLink>
        <header className="ctb-profile-head">
          <ContributorAvatar c={c} size={120} />
          <div>
            <p className="ctb-eyebrow">Contributeur</p>
            <h1>{c.name}</h1>
            {c.role && <p className="ctb-role">{c.role}</p>}
            <p className="ctb-count">
              {plural(c.articles, 'article')} publié{c.articles > 1 ? 's' : ''}
            </p>
          </div>
        </header>
        {c.bio && (
          <div className="ctb-profile-bio prose">
            {c.bio
              .split(/\n\s*\n/)
              .filter((p) => p.trim())
              .map((p, i) => (
                <p key={i}>{p.trim()}</p>
              ))}
          </div>
        )}
        <h2 className="section-title ctb-articles-title">Ses articles</h2>
        {!articles.data ? (
          <Loading />
        ) : (
          <div className={isMag ? 'mg-grid' : 'grid cols-3'}>
            {articles.data.articles.map((a) =>
              isMag ? (
                <Card key={a.id} article={a} variant="tile" />
              ) : (
                <ArticleCard key={a.id} article={a} variant="card" />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** Encadré « À propos de l'auteur », en bas d'un article. */
export function AuthorBox({ name }: { name: string }) {
  const path = usePubliqApi()
  const slug = slugify(name)
  const data = useFetch<{ contributor: Contributor }>(slug ? path(`/contributors/${slug}`) : null)
  const c = data.data?.contributor
  if (!c) return null
  return (
    <aside className="ctb-authorbox" aria-label="À propos de l’auteur">
      <ContributorAvatar c={c} size={72} />
      <div>
        <p className="ctb-eyebrow">À propos de l’auteur</p>
        <strong>{c.name}</strong>
        {c.role && <p className="ctb-role">{c.role}</p>}
        {c.bio && <p className="ctb-bio">{excerpt(c.bio, 260)}</p>}
        <SmartLink href={`/contributeurs/${c.slug}`} className="ctb-more">
          Voir la fiche et ses {plural(c.articles, 'article')} →
        </SmartLink>
      </div>
    </aside>
  )
}
